import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface SchoolInfoSnapshot {
  schoolName: string;
  schoolImage: string;
  state: string;
  locality: string;
}

// Bridges HeaderV2Component (which still owns the actual isValidToken() →
// isValidSchoolId() → schoolInfo() API chain and writes the result to
// localStorage['schoolAllInfo']) and HeaderV3Component (the header that's
// actually shown — see layout.component.html). HeaderV3 used to read
// schoolAllInfo from localStorage exactly once, in its own ngOnInit; since
// HeaderV2's API chain finishes asynchronously, often after login's SPA
// navigation has already mounted both headers, HeaderV3's one-time read
// would find nothing and never re-check — only a full page refresh
// (re-mounting HeaderV3 after localStorage was already populated) ever
// showed the real school name. This service lets HeaderV2 push the result
// the moment it arrives, and HeaderV3 subscribe instead of reading once.
@Injectable({ providedIn: 'root' })
export class SchoolInfoService {
  private readonly subject = new BehaviorSubject<SchoolInfoSnapshot | null>(this.readFromCache());

  readonly schoolInfo$ = this.subject.asObservable();

  get snapshot(): SchoolInfoSnapshot | null {
    return this.subject.value;
  }

  set(info: SchoolInfoSnapshot): void {
    this.subject.next(info);
  }

  private readFromCache(): SchoolInfoSnapshot | null {
    const raw = localStorage.getItem('schoolAllInfo');
    if (!raw) {
      return null;
    }
    try {
      const schoolData = JSON.parse(raw);
      return {
        schoolName: schoolData?.schoolName || '',
        schoolImage: schoolData?.schoolImage || '',
        state: schoolData?.state || '',
        locality: schoolData?.locality || '',
      };
    } catch {
      return null;
    }
  }
}
